import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api";
import { usePermissions } from "../permissions";
import { Card, ErrorNotice, Loading } from "./Ui";
import type { Overview } from "./CapacityPage";
import { WorkMix } from "./DeliveryOverview";
import { formatDate, label } from "../utils";

type ProjectLink = { sourceId: string; projectId: string };
type Links = { initiativeId: number; version: number; links: ProjectLink[] };
function useDelivery() {
  const api = useApi();
  const permissions = usePermissions();
  return useQuery({
    queryKey: ["capacity"],
    queryFn: () => api.get<Overview>("/capacity/"),
    enabled: permissions.can("capacity.view"),
    refetchInterval: 60000,
  });
}
export function DeliveryDashboard() {
  const permissions = usePermissions();
  const data = useDelivery();
  if (!permissions.can("capacity.view")) return null;
  return (
    <Card className="span-2">
      <div className="card-header">
        <h2>Delivery capacity</h2>
        <Link to="/capacity">View capacity details</Link>
      </div>
      {data.isPending ? (
        <Loading />
      ) : data.isError ? (
        <ErrorNotice error={data.error} />
      ) : (
        <>
          {!data.data.sources.length && (
            <p>No delivery connections configured.</p>
          )}
          {data.data.sources.map((source) => {
            const health = data.data.items.find(
              (p) =>
                p.department === source.department &&
                p.period === source.period,
            );
            const stale =
              !source.syncedAt ||
              Date.now() - Date.parse(source.syncedAt) > 1800000;
            return (
              <section key={source.id}>
                <h3>
                  {source.department} ·{" "}
                  {health ? label(health.status) : "Unknown"}
                </h3>
                <p className="section-help">
                  Last imported {formatDate(source.syncedAt)} ·{" "}
                  {stale ? "Stale — sync required" : "Recent snapshot"}
                </p>
                {source.delivery ? (
                  <>
                    <p>
                      {source.delivery.currentCount} current-sprint items ·{" "}
                      {source.delivery.completedCount} completed ·{" "}
                      {source.delivery.forecast.remainingItems}{" "}
                      capacity-eligible unfinished items
                    </p>
                    <p>
                      Eligible work:{" "}
                      {source.delivery.committedWarnings?.Blocked ?? 0} blocked
                      · {source.delivery.committedWarnings?.Overdue ?? 0}{" "}
                      overdue ·{" "}
                      {source.delivery.committedWarnings?.[
                        "Missing estimate"
                      ] ?? 0}{" "}
                      missing estimates
                    </p>
                    <WorkMix summary={source.delivery} stale={stale} />
                  </>
                ) : (
                  <p>
                    Delivery snapshot unavailable. Enable and sync the
                    connection in Capacity.
                  </p>
                )}
              </section>
            );
          })}
        </>
      )}
    </Card>
  );
}
export function LinkedDelivery({
  initiativeId,
  editable = false,
}: {
  initiativeId: number;
  editable?: boolean;
}) {
  const api = useApi();
  const permissions = usePermissions();
  const overview = useDelivery();
  const links = useQuery({
    queryKey: ["delivery-links"],
    queryFn: () => api.get<{ items: Links[] }>("/capacity/initiative-links"),
    enabled: permissions.can("capacity.view"),
  });
  if (!permissions.can("capacity.view")) return null;
  if (overview.isPending || links.isPending) return <Loading />;
  if (overview.isError) return <ErrorNotice error={overview.error} />;
  if (links.isError) return <ErrorNotice error={links.error} />;
  const saved = links.data.items.find(
    (v) => v.initiativeId === initiativeId,
  ) ?? { initiativeId, version: 0, links: [] };
  return (
    <section aria-label="Linked delivery projects">
      {!saved.links.length && (
        <p className="muted">No Linear projects linked.</p>
      )}
      {saved.links.map((link) => {
        const source = overview.data.sources.find(
          (s) => s.id === link.sourceId,
        );
        const project = source?.delivery?.projects.find(
          (p) => p.id === link.projectId,
        );
        const stale =
          !source?.syncedAt ||
          Date.now() - Date.parse(source.syncedAt) > 1800000;
        return (
          <div key={`${link.sourceId}:${link.projectId}`}>
            <strong>{project?.name ?? "Linked project unavailable"}</strong>
            {project && (
              <p>
                {label(project.category)} · {project.open} open ·{" "}
                {project.completed} completed this cycle · {project.committed}{" "}
                capacity-eligible · {project.missing} missing estimates
              </p>
            )}
            <p className="section-help">
              {stale ? "Stale snapshot" : "Snapshot"} ·{" "}
              {formatDate(source?.syncedAt ?? null)}
            </p>
          </div>
        );
      })}
      {editable && permissions.can("integrations.manage") && (
        <details>
          <summary>Manage linked Linear projects</summary>
          <LinkEditor
            key={saved.version}
            initial={saved}
            overview={overview.data}
          />
        </details>
      )}
    </section>
  );
}
function LinkEditor({
  initial,
  overview,
}: {
  initial: Links;
  overview: Overview;
}) {
  const api = useApi();
  const client = useQueryClient();
  const [links, setLinks] = useState(initial.links);
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api.put(`/capacity/initiatives/${initial.initiativeId}/projects`, {
        version: initial.version,
        links,
        changeReason: reason,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["delivery-links"] }),
  });
  const options = overview.sources.flatMap((s) =>
    (s.delivery?.projects ?? [])
      .filter((p) => p.id !== "none")
      .map((p) => ({
        sourceId: s.id,
        projectId: p.id,
        name: `${s.department} — ${p.name}`,
      })),
  );
  for (const link of initial.links)
    if (
      !options.some(
        (p) => p.sourceId === link.sourceId && p.projectId === link.projectId,
      )
    )
      options.push({
        ...link,
        name: `Unavailable project (${link.projectId})`,
      });
  return (
    <form
      className="portfolio-form"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="section-help">
        Select one or more projects. Links do not change strategic scores or
        approvals. A project linked to multiple initiatives is not counted twice
        in department capacity.
      </p>
      {options.map((p) => (
        <label key={`${p.sourceId}:${p.projectId}`}>
          <input
            type="checkbox"
            checked={links.some(
              (l) => l.sourceId === p.sourceId && l.projectId === p.projectId,
            )}
            onChange={(e) =>
              setLinks((old) =>
                e.target.checked
                  ? [...old, { sourceId: p.sourceId, projectId: p.projectId }]
                  : old.filter(
                      (l) =>
                        l.sourceId !== p.sourceId ||
                        l.projectId !== p.projectId,
                    ),
              )
            }
          />
          {p.name}
        </label>
      ))}
      {!options.length && <p>Sync a Linear connection to load projects.</p>}
      <label>
        Change reason
        <input
          required
          minLength={3}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button className="btn-primary" disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save project links"}
      </button>
      {save.isError && <ErrorNotice error={save.error} />}
    </form>
  );
}
